import { ComponentPropsWithoutRef, forwardRef, ReactNode } from "react";
import clsx from "clsx";
import ErrorBoundary from "../error-boundary";

import styles from "./flex-grid.module.scss";

const alignments = {
  left: "flexGridLeft",
  right: "flexGridRight",
  center: "flexGridCenter",
  justify: "flexGridJustify",
};

const verticalAlignments = {
  top: "flexGridTop",
  bottom: "flexGridBottom",
  middle: "flexGridMiddle",
  stretch: "flexGridStretch",
};

const directions = {
  horizontal: "flexGridHorizontal",
  vertical: "flexGridVertical",
};

type Alignments = typeof alignments;
type VerticalAlignments = typeof verticalAlignments;
type Directions = typeof directions;

type FlexGridProps = {
  /** Which horizontal flex grid alignment to use */
  alignment?: keyof Alignments;
  /** Which vertical flex grid alignment to use */
  verticalAlignment?: keyof VerticalAlignments;
  /** The direction of the flex grid */
  direction?: keyof Directions;
  /** Make the grid wrap if necessary */
  wrap?: boolean;
  /** Optional extra class(es) */
  className?: string;
  /** The items of the grid, either FlexGridItem[] or any React node. */
  children: ReactNode;
} & ComponentPropsWithoutRef<"div">;

/**
 *
 *
 *
 *
 */
const _FlexGrid = forwardRef<HTMLDivElement, FlexGridProps>(
  (
    {
      alignment = "justify",
      verticalAlignment = "stretch",
      direction = "horizontal",
      wrap,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const alignmentClass = alignments[alignment];
    const verticalAligmentClass = verticalAlignments[verticalAlignment];
    const directionClass = directions[direction];
    const wrapClass = wrap && styles.flexGridWrap;

    const classes = [
      styles.flexGrid,
      styles[alignmentClass],
      styles[verticalAligmentClass],
      styles[directionClass],
      wrapClass,
      className,
    ];

    return (
      <div ref={ref} {...rest} className={clsx(classes)}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </div>
    );
  }
);

const FlexGrid = Object.assign(_FlexGrid, {
  alignments: Object.keys(alignments).reduce(
    (obj, key) => ({ ...obj, [key]: key }),
    {}
  ) as RecordKeys<Alignments>,
  verticalAlignments: Object.keys(verticalAlignments).reduce(
    (obj, key) => ({ ...obj, [key]: key }),
    {}
  ) as RecordKeys<VerticalAlignments>,
  directions: Object.keys(directions).reduce(
    (obj, key) => ({ ...obj, [key]: key }),
    {}
  ) as RecordKeys<Directions>,
});

FlexGrid.displayName = "FlexGrid";

export default FlexGrid;
